"use client";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Link from "next/link";
import { Store } from "lucide-react";
import { LgsLogo } from "modules/lgs/components/LgsLogo";
import { ICON_SIZE } from "modules/common/components/navigation/Menu";
import { CityItemStore } from "modules/city/model/cityCards";
import { TradeItem, TradeListType } from "modules/tradeLists/model/tradeList";
import { useFacetSearchParamsContract } from "modules/common/hooks/useFacetSearchParamsContract";

interface StoreDropdownButtonProps {
  stores: CityItemStore[];
  item?: TradeItem;
  size?: "xs" | "sm" | "md";
}

const btnSizeClasses = {
  xs: "btn-xs",
  sm: "btn-sm",
  md: "btn-md",
};

export function StoreDropdownButton({
  stores,
  item,
  size = "sm",
}: StoreDropdownButtonProps) {
  const { serializeLGSParams } = useFacetSearchParamsContract();
  const [isOpen, setIsOpen] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });
  const [positioned, setPositioned] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);

  const calculatePosition = useCallback(() => {
    if (!buttonRef.current) return;

    const buttonRect = buttonRef.current.getBoundingClientRect();
    const menuWidth = 256; // w-64 = 256px
    const padding = 8;

    let top = buttonRect.bottom + padding;
    let left = buttonRect.right - menuWidth;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    if (left + menuWidth > viewportWidth - padding) {
      left = viewportWidth - menuWidth - padding;
    }
    if (left < padding) {
      left = padding;
    }

    const menuHeight = menuRef.current?.offsetHeight ?? 200;
    if (top + menuHeight > viewportHeight - padding) {
      top = buttonRect.top - menuHeight - padding;
    }
    if (top < padding) {
      top = padding;
    }

    setMenuPos({ top, left });
    setPositioned(true);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        buttonRef.current &&
        menuRef.current &&
        !buttonRef.current.contains(event.target as Node) &&
        !menuRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setPositioned(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      calculatePosition();
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [calculatePosition, isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleReposition = () => calculatePosition();
    window.addEventListener("scroll", handleReposition, true);
    window.addEventListener("resize", handleReposition);

    return () => {
      window.removeEventListener("scroll", handleReposition, true);
      window.removeEventListener("resize", handleReposition);
    };
  }, [calculatePosition, isOpen]);

  if (stores.length === 0) {
    return null;
  }

  const totalStores = stores.length;

  const handleButtonClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isOpen) {
      setPositioned(false);
    }
    setIsOpen(!isOpen);
  };

  const closeMenu = () => {
    setIsOpen(false);
    setPositioned(false);
  };

  const renderPortalMenu = () => {
    if (!isOpen || typeof document === "undefined") return null;

    return createPortal(
      <ul
        ref={menuRef}
        className="menu rounded-box border-base-content/20 bg-base-100 fixed z-[9999] w-64 border p-2 shadow-lg"
        style={{
          top: `${menuPos.top}px`,
          left: `${menuPos.left}px`,
          opacity: positioned ? 1 : 0,
        }}
      >
        {/* Header */}
        <div className="border-base-content/20 mb-2 border-b px-2 py-2">
          <span className="text-sm font-semibold">
            {item?.list?.type === TradeListType.TRADES ? "Available" : "Wanted"}{" "}
            at {totalStores} store{totalStores > 1 ? "s" : ""}
          </span>
        </div>

        {/* Store list */}
        {stores.map((store) => (
          <li key={store.wotcId}>
            <Link
              href={
                item?.name
                  ? serializeLGSParams(`/stores/${store.slug}`, {
                      query: item.name,
                    })
                  : `/stores/${store.slug}`
              }
              className="flex items-center justify-between gap-2 text-sm"
              onClick={closeMenu}
            >
              <div className="flex items-center gap-2">
                <LgsLogo
                  wotcId={store.wotcId}
                  storeName={store.name}
                  isTradingHub={store.isTradingHub}
                  size="xs"
                  fallbackClassName="text-current"
                />
                <span className="truncate">{store.name}</span>
              </div>
              {store.quantity > 1 && (
                <span className="badge badge-primary badge-xs">
                  {store.quantity}
                </span>
              )}
            </Link>
          </li>
        ))}
      </ul>,
      document.body
    );
  };

  return (
    <div className="relative">
      <div
        ref={buttonRef}
        tabIndex={0}
        role="button"
        className={`btn btn-circle btn-ghost ${btnSizeClasses[size]} cursor-pointer`}
        onClick={handleButtonClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleButtonClick(e as unknown as React.MouseEvent);
          }
        }}
        aria-label={`View ${totalStores} store${totalStores > 1 ? "s" : ""}`}
        aria-expanded={isOpen}
        aria-haspopup="menu"
      >
        <div className="indicator">
          <Store size={ICON_SIZE} className="text-primary" />
          <span className="badge indicator-item badge-primary badge-xs">
            {totalStores}
          </span>
        </div>
      </div>
      {renderPortalMenu()}
    </div>
  );
}
